import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, X, Loader2 } from "lucide-react";
import { useApp, API } from "@/context/AppContext";

const uid = () => "sess-" + Math.random().toString(36).slice(2) + Date.now();
const getSession = () => {
  let s = localStorage.getItem("chatSession");
  if (!s) { s = uid(); localStorage.setItem("chatSession", s); }
  return s;
};

const STR = {
  en: { title: "AI Tyre Assistant", sub: "Ask about sizes, brands & quotes", ph: "Type your message...", greet: "Hi! I'm your tyre assistant. Ask me about tyre sizes, brands, categories or how to get a quotation.", err: "Sorry, something went wrong. Please try again or contact us on WhatsApp." },
  ar: { title: "مساعد الإطارات الذكي", sub: "اسأل عن المقاسات والماركات والأسعار", ph: "اكتب رسالتك...", greet: "مرحباً! أنا مساعد الإطارات. اسألني عن مقاسات الإطارات أو الماركات أو الفئات أو كيفية الحصول على عرض سعر.", err: "عذراً، حدث خطأ. حاول مرة أخرى أو تواصل معنا عبر واتساب." },
};

const ChatWidget = () => {
  const { lang } = useApp();
  const s = STR[lang];
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(getSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((p) => [...p, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text, history });
      setMessages((p) => [...p, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((p) => [...p, { role: "assistant", content: s.err }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="chat-open-button"
        aria-label={s.title}
        className="fixed bottom-24 end-6 z-50 flex items-center justify-center h-14 w-14 rounded-full bg-[#EAB308] text-[#050505] shadow-2xl transition-transform hover:scale-110"
      >
        <Bot className="h-7 w-7" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 end-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] max-h-[75vh] bg-[#0a0a0a] border border-[#27272a] flex flex-col shadow-2xl"
          >
            {/* header */}
            <div className="flex items-center justify-between p-4 border-b border-[#27272a] bg-[#121212]">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 flex items-center justify-center bg-[#EAB308] text-[#050505]"><Bot className="h-5 w-5" /></div>
                <div>
                  <p className="text-white font-bold text-sm leading-tight">{s.title}</p>
                  <p className="text-zinc-500 text-xs">{s.sub}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close-button" className="text-zinc-400 hover:text-white" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>

            {/* messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              <div className="flex gap-2">
                <div className="h-7 w-7 shrink-0 flex items-center justify-center bg-[#EAB308]/15 border border-[#EAB308]/30 text-[#EAB308]"><Bot className="h-4 w-4" /></div>
                <div className="bg-[#121212] border border-[#27272a] px-3 py-2 text-sm text-zinc-200 leading-relaxed max-w-[80%]">{s.greet}</div>
              </div>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "gap-2"}`}>
                  {m.role === "assistant" && <div className="h-7 w-7 shrink-0 flex items-center justify-center bg-[#EAB308]/15 border border-[#EAB308]/30 text-[#EAB308]"><Bot className="h-4 w-4" /></div>}
                  <div className={`px-3 py-2 text-sm leading-relaxed max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-[#EAB308] text-[#050505]" : "bg-[#121212] border border-[#27272a] text-zinc-200"}`} dir="auto">
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-2">
                  <div className="h-7 w-7 shrink-0 flex items-center justify-center bg-[#EAB308]/15 border border-[#EAB308]/30 text-[#EAB308]"><Bot className="h-4 w-4" /></div>
                  <div className="bg-[#121212] border border-[#27272a] px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-zinc-400" /></div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* input */}
            <div className="p-3 border-t border-[#27272a] bg-[#121212] flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={s.ph}
                data-testid="chat-input"
                dir="auto"
                className="flex-1 bg-[#0a0a0a] border border-[#27272a] text-white px-3 py-2.5 text-sm outline-none focus:border-[#EAB308] transition-colors placeholder:text-zinc-600"
              />
              <button onClick={send} disabled={loading || !input.trim()} data-testid="chat-send-button"
                className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#EAB308] text-[#050505] hover:bg-[#ca8a04] transition-colors disabled:opacity-50">
                <Send className="h-4 w-4 rtl:rotate-180" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
